import type { StandardAnswerValue } from '../../../report/questions/ReportQuestion';
import type { Location, LocationV2 } from './Location';
import type { ConfirmationDetail } from './messageDetail/ConfirmationDetail';
import type { NotificationMethod } from './NotificationMethod';
import type { PremiseStatusV2 } from './PremiseStatusV2';
import type { Reporter } from './Reporter';
import type { TraceableMessage } from './TraceableMessage';

// Used for V1 symptoms interface only
export enum PoleEnd {
	Pole = 'Pole',
	Building = 'Building',
	Both = 'Both',
}

export interface VersionedSymptoms {
	version?: number;
}

export enum PoleType {
	Wood = 'Wood',
	Metal = 'Metal',
}

export enum WirePosition {
	Low = 'Low',
	Down = 'Down',
	WithinReach = 'Within Reach',
}

export enum EndDown {
	Pole = 'Pole',
	Building = 'Building',
	Unknown = 'Unknown',
}

export enum ProductType {
	Electric = 'Electric',
	Gas = 'Gas',
}

export interface WireIssue extends Issue {
	/**
	 * Position of the wire
	 *
	 * Includes common values of "Low", "Down", or "Within Reach"
	 */
	position?: (WirePosition | StandardAnswerValue | string)[];

	/**
	 * Pass if the wire is on something
	 */
	wireOnObject?: (StandardObject | string)[];

	//{
	/**
	 * List of anything the wire is on
	 *
	 * "Ground"
	 * "Road"
	 * "Metal Fence"
	 * "Vehicle"
	 * "Roof"
	 * (freeform response)
	 */
	//type?: (StandardObject | string)[];
	//};

	/**
	 * Pass if something is on the wire
	 */
	objectOnWire?: (StandardObject | string)[];
	//{
	/**
	 * List of anything that's on a wire
	 *
	 * Examples:
	 * "Tree or Limb"
	 * (freeform response)
	 */
	//type?: (StandardObject | string)[];
	//};

	peeling?: boolean;
}

export interface PoleToBuildingWireIssue extends WireIssue {
	endDown?: EndDown;
}

export enum StandardObject {
	Meter = 'Meter',
	Building = 'Building',
	Pole = 'Pole',
	Transformer = 'Transformer',
	UndergroundTransformer = 'Underground Transformer',
	PowerLine = 'Power Line',
	Tree = 'Tree',
	MetalFence = 'Metal Fence',
	Ground = 'Ground',
	Roof = 'Roof',
	Vehicle = 'Vehicle',
	Road = 'Road',
	BrushPickUp = 'Brush Pickup',
	TreeTrunks = 'Tree Trunks',
	PiecesOfTree = 'Pieces Of Tree',
}

export interface Issue {
	location?: (StandardObject | string)[];
	details?: string;
}

export interface PowerIssue extends Issue {
	outage?: boolean;
	partialOutage?: boolean;
	dimOrBrightLights?: boolean;
	flickeringLights?: boolean;
}

export interface PoleIssue extends Issue {
	type?: PoleType | StandardAnswerValue;
	broken?: boolean;
	brokenGuyWire?: boolean;
	leaning?: boolean;
}

export interface ArcingIssue extends Issue {}

export interface SafetyIssue extends Issue {
	contactWithEnergizedEquipment?: boolean;
	publicTrapped?: boolean;
	injuries?: boolean;
	policeFireStandingBy?: boolean;
	trafficSignal?: {
		out?: boolean;
		blinking?: boolean;
	};
	nearSchoolOrPlayground?: boolean;
	accessibleToPublic?: boolean;
}

export interface ShutoffIsssue extends Issue {}

export interface FireIssue extends Issue {}

export enum CauseSource {
	Construction = 'Construction',
	Vehicle = 'Vehicle',
	Vandalism = 'Vandalism',
	Animal = 'Animal',
	Weather = 'Weather',
	Tree = 'Tree',
	Unknown = 'Unknown',
}

export interface ProblemCause {
	source?: CauseSource[];
	details?: string;
	responsibleParty?: string;
}

export interface SymptomsV2 extends VersionedSymptoms {
	version: 2;

	/**
	 * Sent if there's something wrong with power
	 */
	power?: PowerIssue;

	/**
	 * Send object if there are issues with a pole to pole wire
	 */
	poleToPoleWire?: WireIssue;

	/**
	 * Send object if there are issues with a pole to building wire
	 */
	poleToBuildingWire?: PoleToBuildingWireIssue;

	/**
	 * Send object if there are issues with a broken pole
	 */
	pole?: PoleIssue;

	/**
	 * Pass object if something is arcing
	 */
	arcing?: ArcingIssue;

	/**
	 * Pass object is shutoff is needed somewhere
	 */
	shutoffNeeded?: ShutoffIsssue;

	/**
	 * Pass object if something is on fire
	 */
	fire?: FireIssue;

	// Additional details related to safety
	safety?: SafetyIssue;

	/**
	 * Issue with an underground wire
	 */
	undergroundWire?: UndergroundWireIssue;
}

export interface Symptoms extends VersionedSymptoms {
	powerOut?: boolean;
	partialPowerOut?: boolean;
	dimOrBrightLights?: boolean;
	flickeringLights?: boolean;
	wireDownPoleToPole?: boolean;

	/**
	 * Examples:
	 * false / undefined (wire is not down from pole to building)
	 * true (if wire is down from pole to building but the end is unknown or unspecified)
	 * "Pole" (wire is down at only the pole end)
	 * "Building" (wire is down at only the building end)
	 * "Both" (wire is down at both ends)
	 */
	wireDownPoleToBuilding?: boolean | PoleEnd;

	undergroundWireExposed?: boolean;
	arcing?: boolean;
	brokenPole?: boolean;
}

export interface Problem {
	observations?: string | string[];
	symptoms: Symptoms | SymptomsV2;

	/**
	 * Details on what might have caused the issue
	 */
	cause?: ProblemCause;

	notes?: string;

	// List of links for attachments
	attachments?: string[];
}

export interface ProblemV2 extends Problem {
	symptoms: SymptomsV2;
}

export interface CustomerReportData extends TraceableMessage {
	// For older records, this won't be set
	version?: number;

	reportId: string;

	reportedBy?: Reporter;

	// Call id (will only be sent when things have already been reported from CRM)
	callId?: string;

	eventTime: string;
	channel: string;
	contactPhoneNumber?: string;
	location: Location | LocationV2;

	problem: Problem;
	notifications?: NotificationMethod[];
}

export interface CustomerReportDataV2 extends CustomerReportData {
	location: LocationV2;
	problem: ProblemV2;
}

export interface CustomerReportResponse extends PremiseStatusV2 {
	// TODO: remove code + confirmation once clients have switched to using the report object
	code: string;
	confirmation: string;
	confirmationDetails: ConfirmationDetail;
}

export interface UndergroundWireIssue extends WireIssue {
	exposed?: boolean;
}
